import type { CSSObject } from '@mui/material/styles';

import { Toaster } from 'sonner';
import { varAlpha } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { snackbarClasses } from './classes';

// ----------------------------------------------------------------------

export const SnackbarRoot = styled(Toaster)(({ theme }) => {
  const baseStyles: Record<string, CSSObject> = {
    toastDefault: {
      padding: theme.spacing(1, 1, 1, 1.5),
      boxShadow: theme.vars.customShadows.z8,
      color: theme.vars.palette.background.paper,
      backgroundColor: theme.vars.palette.text.primary,
    },
    toastColor: {
      padding: theme.spacing(0.5, 1, 0.5, 0.5),
      boxShadow: theme.vars.customShadows.z8,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.paper,
    },
    toastLoader: {
      padding: theme.spacing(0.5, 1, 0.5, 0.5),
      boxShadow: theme.vars.customShadows.z8,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.paper,
    },
  };

  const loadingStyles: CSSObject = {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'none',
    transform: 'none',
    overflow: 'hidden',
    alignItems: 'center',
    position: 'relative',
    borderRadius: 'inherit',
    justifyContent: 'center',
    background: theme.vars.palette.background.neutral,
    [`& .${snackbarClasses.loadingIcon}`]: {
      zIndex: 9,
      width: 24,
      height: 24,
      borderRadius: '50%',
      animation: 'rotate 3s infinite linear',
      background: `conic-gradient(transparent, ${varAlpha(theme.vars.palette.text.disabledChannel, 0.64)})`,
    },
    [snackbarClasses.loaderVisible]: { display: 'flex' },
  };

  return {
    [`& .${snackbarClasses.toast}`]: {
      gap: 12,
      width: 'auto',
      minHeight: 48,
      display: 'flex',
      borderRadius: 8,
      alignItems: 'center',
    },
    /**
     * Content
     */
    [`& .${snackbarClasses.content}`]: {
      flex: '1 1 auto',
    },
    [`& .${snackbarClasses.title}`]: {
      fontSize: theme.typography.body2.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
    },
    [`& .${snackbarClasses.description}`]: {
      ...theme.typography.caption,
      opacity: 0.64,
    },
    /**
     * Buttons
     */
    [`& .${snackbarClasses.actionButton}`]: {},
    [`& .${snackbarClasses.cancelButton}`]: {},
    [`& .${snackbarClasses.closeButton}`]: {
      top: '50%',
      right: 8,
      width: 24,
      height: 24,
      padding: 0,
      display: 'flex',
      cursor: 'pointer',
      borderRadius: '50%',
      alignItems: 'center',
      position: 'absolute',
      color: 'currentColor',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      transform: 'translateY(-50%)',
      border: 'none',
      transition: theme.transitions.create(['background-color']),
      '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
      },
      '& > svg': { width: 16, height: 16 },
    },

    /**
     * Icon
     */
    [`& .${snackbarClasses.icon}`]: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [`& .${snackbarClasses.iconSvg}`]: {
        width: 22,
        height: 22,
      },
    },

    '@keyframes rotate': { to: { transform: 'rotate(1turn)' } },

    /**
     * @variant default
     */
    [`& .${snackbarClasses.default}`]: {
      ...baseStyles.toastDefault,
      [`&:has(${snackbarClasses.closeBtnVisible})`]: {
        [`& .${snackbarClasses.content}`]: { paddingRight: 32 },
      },
      [`&:has(.${snackbarClasses.loader})`]: baseStyles.toastLoader,
      /**
       * @with loader
       */
      [`&:has(.${snackbarClasses.loader})`]: baseStyles.toastLoader,
      [`& .${snackbarClasses.loader}`]: loadingStyles,
    },
    /**
     * @variant error
     */
    [`& .${snackbarClasses.error}`]: {
      padding: theme.spacing(1.5, 2),
      boxShadow: 'none',
      color: theme.vars.palette.common.white,
      backgroundColor: theme.vars.palette.error.main,
      [`& .${snackbarClasses.icon}`]: {
        color: theme.vars.palette.common.white,
        backgroundColor: 'transparent',
      },
      [`& .${snackbarClasses.closeButton}`]: {
        color: theme.vars.palette.common.white,
        borderColor: 'transparent',
        '&:hover': {
          backgroundColor: varAlpha(theme.vars.palette.common.whiteChannel, 0.16),
        },
      },
    },
    /**
     * @variant success
     */
    [`& .${snackbarClasses.success}`]: {
      padding: theme.spacing(1.5, 2),
      boxShadow: 'none',
      color: theme.vars.palette.success.dark,
      backgroundColor: theme.vars.palette.success.lighter,
      border: `solid 1px ${theme.vars.palette.success.light}`,
      [`& .${snackbarClasses.icon}`]: {
        color: theme.vars.palette.success.main,
        backgroundColor: 'transparent',
      },
      [`& .${snackbarClasses.iconSvg}`]: {
        width: 20,
        height: 20,
      },
      [`& .${snackbarClasses.title}`]: {
        color: theme.vars.palette.success.dark,
      },
      [`& .${snackbarClasses.description}`]: {
        color: theme.vars.palette.success.dark,
        opacity: 1,
      },
      [`& .${snackbarClasses.closeButton}`]: {
        color: theme.vars.palette.success.dark,
        borderColor: 'transparent',
        '&:hover': {
          backgroundColor: varAlpha(theme.vars.palette.success.mainChannel, 0.16),
        },
      },
    },
    /**
     * @variant warning
     */
    [`& .${snackbarClasses.warning}`]: {
      ...baseStyles.toastColor,
      [`& .${snackbarClasses.icon}`]: {
        color: theme.vars.palette.warning.main,
        backgroundColor: varAlpha(theme.vars.palette.warning.mainChannel, 0.08),
      },
    },
    /**
     * @variant info
     */
    [`& .${snackbarClasses.info}`]: {
      ...baseStyles.toastColor,
      [`& .${snackbarClasses.icon}`]: {
        color: theme.vars.palette.info.main,
        backgroundColor: varAlpha(theme.vars.palette.info.mainChannel, 0.08),
      },
    },
  };
});
